import styled from 'styled-components';
import { fonts } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useNavBarHeight } from 'utils/hooks/useNavBarHeight';
import { SideNavLink } from './SideNavLink';
import { SideNavSection } from './SideNavSection';
import { SideNavLinkConfig, SideNavSectionConfig, SideNavStyle } from './types';
import { isSection } from './utils';

const SideNavWrapper = styled.div`
  font-family: ${fonts.sansSerif};
  min-width: ${pxToRem(208)};
`;

const StickyContainer = styled.ul<{
  isSticky?: boolean;
  stickyPadding: number;
  navbarHeight: number;
}>`
  position: ${(props) => (props.isSticky ? 'sticky' : 'relative')};
  top: ${({ navbarHeight, stickyPadding, isSticky }) =>
    isSticky ? pxToRem(navbarHeight + stickyPadding) : '0'};
  margin: 0;
  padding: 0;
  list-style: none;
`;

type SideNavProps = {
  /** array to define the hierarchy of the nav sections */
  linksAndSections: (SideNavSectionConfig | SideNavLinkConfig)[];
  /** boolean to determine if the sidenav will be sticky */
  isSticky?: boolean;
  /** top padding value to be used if the sidenav is sticky */
  stickyPadding?: number;
  /** the rendering style of the sections of the side nav */
  style?: SideNavStyle;
};

export const SideNav = ({
  linksAndSections = [],
  isSticky,
  stickyPadding = 0,
  style = SideNavStyle.normal,
}: SideNavProps) => {
  const navbarHeight = useNavBarHeight();

  return (
    <SideNavWrapper data-test="side-nav-wrapper">
      <StickyContainer
        data-test="side-nav-list"
        isSticky={isSticky}
        navbarHeight={navbarHeight}
        stickyPadding={stickyPadding}
      >
        {linksAndSections.map((linkOrSection) =>
          isSection(linkOrSection) ? (
            <SideNavSection
              key={linkOrSection.header}
              header={linkOrSection.header}
              style={style}
              links={linkOrSection.links}
            />
          ) : (
            <SideNavLink key={linkOrSection.pathname} {...linkOrSection}>
              {linkOrSection.name}
            </SideNavLink>
          ),
        )}
      </StickyContainer>
    </SideNavWrapper>
  );
};
